import { BlogArticle } from '../types';

// ============================================================================
// DEMO ARTICLE — 「聽日默書點算好？5 個默書急救法」
//
// This file shows the article format: metadata above, one HTML body per
// language below, and an optional `css` block with styles only this article
// uses (here: the .tip-box callout). To publish a new article, copy this
// file, change the slug, and register it in `articles/index.ts`.
// ============================================================================

const article: BlogArticle = {
  slug: 'dictation-last-minute-tips',
  date: '2026-09-22',
  readingMinutes: 4,
  tags: ['默書', 'Dictation', '溫書技巧', 'DSE'],

  // Per-article CSS — only injected while this article is on screen.
  css: `
    .tip-box {
      background: linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%);
      border-left: 4px solid #6366f1;
      border-radius: 10px;
      padding: 14px 18px;
      margin: 22px 0;
    }
    .tip-box strong { color: #4f46e5; }
  `,

  locales: {
    // ----------------------------------------------------------------------
    zhHant: {
      title: '聽日默書點算好？5 個默書急救法',
      excerpt:
        '聽日就默書，今日先開始溫？唔使驚！呢 5 個急救方法，幫你用最短時間記熟生字，仲可以免費模擬默書環境。',
      keywords: '默書, 聽寫, 默書技巧, 溫書方法, 英文生字, DSE',
      contentHtml: `
        <p>聽日就默書，但生字仲未記熟？與其臨急抱佛腳亂溫一通，不如用啱方法，將溫習效率推到最高。以下 5 個急救法，專為「最後衝刺」而設。</p>

        <h2>1. 拆解生字，分段記憶</h2>
        <p>長生字最難記，但只要拆成音節就易好多。例如 <em>necessary</em> 可以拆成 <em>ne-ces-sa-ry</em>，一邊讀一邊寫，記憶會牢固好多。</p>

        <h2>2. 「讀 → 寫 → 蓋 → 默」四步循環</h2>
        <ol>
          <li><strong>讀</strong>：望住個字讀出聲。</li>
          <li><strong>寫</strong>：跟住寫兩次。</li>
          <li><strong>蓋</strong>：遮住個字。</li>
          <li><strong>默</strong>：憑記憶默出嚟，再對答案。</li>
        </ol>
        <p>每個字重複 3 個循環，比起一味抄寫 20 次更有效。</p>

        <div class="tip-box">
          <strong>💡 貼士：</strong>心理學研究發現，「主動回憶」（Active Recall）的記憶效果是重複閱讀的兩倍以上。默書急救的核心，就係逼自己回憶，而唔係重溫。
        </div>

        <h2>3. 善用睡前 + 醒來的黃金時間</h2>
        <p>睡前 30 分鐘溫一次，訓覺時大腦會幫你鞏固記憶；醒來再快速過一次，效果事半功倍。</p>

        <h2>4. 模擬真實默書環境</h2>
        <p>自己溫書同真正默書最大分別，係「聽」而唔係「睇」。用工具將生字讀出嚟，模擬老師默書的情境，先知道自己真係識唔識：</p>
        <p><a class="blog-cta-button" href="/module/dictation">🎯 免費試用 StudyMind 默書練習 →</a></p>

        <h2>5. 錯字簿：溫「錯」唔溫「啱」</h2>
        <p>已經識嘅字唔使再浪費時間。將默錯嘅字記落錯字簿，最後衝刺只溫呢啲，先係最高效做法。</p>

        <h2>總結</h2>
        <table>
          <thead>
            <tr><th>時間</th><th>行動</th></tr>
          </thead>
          <tbody>
            <tr><td>今晚 30 分鐘</td><td>四步循環 + 模擬默書一次</td></tr>
            <tr><td>睡前</td><td>快速重溫錯字</td></tr>
            <tr><td>聽朝</td><td>再默一次錯字，執生！</td></tr>
          </tbody>
        </table>
        <blockquote>默書唔係靠天份，係靠方法。祝聽日默書順利！💪</blockquote>
      `,
    },

    // ----------------------------------------------------------------------
    zhHans: {
      title: '明天就默书怎么办？5 个急救方法',
      excerpt:
        '明天就要默书，今天才开始复习？别慌！这 5 个急救方法帮你用最短时间记熟生字，还能免费模拟默写环境。',
      keywords: '默写, 听写, 默写技巧, 复习方法, 英文单词',
      contentHtml: `
        <p>明天就要默书，但单词还没记熟？与其临时抱佛脚乱复习，不如用对方法，把复习效率推到最高。以下 5 个急救法，专为「最后冲刺」而设。</p>

        <h2>1. 拆解单词，分段记忆</h2>
        <p>长单词最难记，但只要拆成音节就容易得多。例如 <em>necessary</em> 可以拆成 <em>ne-ces-sa-ry</em>，一边读一边写，记忆会牢固很多。</p>

        <h2>2. 「读 → 写 → 盖 → 默」四步循环</h2>
        <ol>
          <li><strong>读</strong>：看着单词读出声。</li>
          <li><strong>写</strong>：跟着写两遍。</li>
          <li><strong>盖</strong>：遮住单词。</li>
          <li><strong>默</strong>：凭记忆默出来，再对答案。</li>
        </ol>
        <p>每个单词重复 3 个循环，比一味抄写 20 遍更有效。</p>

        <div class="tip-box">
          <strong>💡 提示：</strong>心理学研究发现，「主动回忆」（Active Recall）的记忆效果是重复阅读的两倍以上。默写急救的核心，就是逼自己回忆，而不是重读。
        </div>

        <h2>3. 善用睡前 + 醒来的黄金时间</h2>
        <p>睡前 30 分钟复习一次，睡觉时大脑会帮你巩固记忆；醒来再快速过一遍，效果事半功倍。</p>

        <h2>4. 模拟真实默写环境</h2>
        <p>自己复习和真正默写最大的区别，是「听」而不是「看」。用工具把单词读出来，模拟老师报听写的情境，才知道自己是不是真的会：</p>
        <p><a class="blog-cta-button" href="/module/dictation">🎯 免费试用 StudyMind 听写练习 →</a></p>

        <h2>5. 错字本：只复习「错」的</h2>
        <p>已经会的字不用再浪费时间。把默错的字记进错字本，最后冲刺只复习这些，才是最高效的做法。</p>

        <h2>总结</h2>
        <table>
          <thead>
            <tr><th>时间</th><th>行动</th></tr>
          </thead>
          <tbody>
            <tr><td>今晚 30 分钟</td><td>四步循环 + 模拟默写一次</td></tr>
            <tr><td>睡前</td><td>快速重温错字</td></tr>
            <tr><td>明早</td><td>再默一次错字，搞定！</td></tr>
          </tbody>
        </table>
        <blockquote>默写不靠天分，靠方法。祝明天默写顺利！💪</blockquote>
      `,
    },

    // ----------------------------------------------------------------------
    en: {
      title: "Dictation Tomorrow? 5 Last-Minute Rescue Tips",
      excerpt:
        "Dictation is tomorrow and you haven't started? Don't panic — these 5 rescue methods help you memorise words fast, plus a free tool to simulate the real thing.",
      keywords: 'dictation, spelling practice, study tips, active recall, exam prep',
      contentHtml: `
        <p>Dictation is tomorrow and the words still aren't sticking? Instead of panicking and re-reading the list, use the right methods to squeeze the most out of the time you have left. Here are 5 rescue tips built for last-minute prep.</p>

        <h2>1. Break words into chunks</h2>
        <p>Long words are hard to memorise — until you split them into syllables. <em>necessary</em> becomes <em>ne-ces-sa-ry</em>. Say each chunk out loud as you write it.</p>

        <h2>2. The Read → Write → Cover → Recall loop</h2>
        <ol>
          <li><strong>Read</strong> the word out loud.</li>
          <li><strong>Write</strong> it twice.</li>
          <li><strong>Cover</strong> it.</li>
          <li><strong>Recall</strong>: write it from memory, then check.</li>
        </ol>
        <p>Three loops per word beat copying it 20 times.</p>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Research shows active recall is more than twice as effective as re-reading. Last-minute prep is about forcing yourself to remember — not reviewing.
        </div>

        <h2>3. Use the golden windows: bedtime + wake-up</h2>
        <p>Review for 30 minutes before sleep — your brain consolidates memory overnight — then do one quick pass in the morning.</p>

        <h2>4. Simulate the real dictation</h2>
        <p>Self-study is reading; real dictation is <em>listening</em>. Have the words read aloud to you to find out what you actually know:</p>
        <p><a class="blog-cta-button" href="/module/dictation">🎯 Try StudyMind's free dictation practice →</a></p>

        <h2>5. Keep a mistake list</h2>
        <p>Don't waste time on words you already know. Log every mistake and spend your final sprint only on those.</p>

        <h2>Your battle plan</h2>
        <table>
          <thead>
            <tr><th>When</th><th>What</th></tr>
          </thead>
          <tbody>
            <tr><td>Tonight (30 min)</td><td>The 4-step loop + one full mock dictation</td></tr>
            <tr><td>Before bed</td><td>Quick pass over your mistake list</td></tr>
            <tr><td>Tomorrow morning</td><td>Re-test the mistakes only. Done!</td></tr>
          </tbody>
        </table>
        <blockquote>Dictation isn't about talent — it's about method. Good luck tomorrow! 💪</blockquote>
      `,
    },
  },
};

export default article;
